import { getSupabase } from "../supabase";

import { entityTableFor, isCountStale } from "./entityRows.mjs";

/**
 * MER-198: data layer for the Customer Detail page.
 *
 * Reads everything from `sync_log` (added by migration 0015) rather
 * than querying canonical tables one-by-one. The pipeline writes a
 * sync_log row per (tenant, entity) sync attempt with timing + row
 * counts + status, so it's a single source of truth for "is this
 * customer's data fresh and how many rows landed last time."
 *
 * Connector status (Navusoft / Samsara / Ramp / uAttend) is inferred
 * from which entity_keys have recent successful syncs; see
 * `CONNECTOR_REGISTRY` for the mapping. A richer connector status
 * panel reading from PLATFORM_STATE.yaml is a v2 follow-up per the
 * ticket "build-time bake" decision.
 */

export interface EntityHealthRow {
  entityKey: string;
  /** started_at of the latest attempt (MER-478: shown for failed rows). */
  lastSyncStartedAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: "success" | "failed" | null;
  rowsUpserted: number | null;
  rowsDeleted: number | null;
  lastErrorDetail: string | null;
}

export interface RecentSyncEvent {
  id: string;
  entityKey: string;
  startedAt: string;
  completedAt: string | null;
  status: "success" | "failed";
  rowsUpserted: number;
  rowsDeleted: number;
  errorDetail: string | null;
}

// MER-477: connector rollup lives in connectorHealth.mjs (case-insensitive
// entity_key matching, unit-tested by scripts/connector-health.test.mjs).
// Re-exported here so page-level imports stay on lib/admin/health.
export { CONNECTOR_REGISTRY, deriveConnectorHealth } from "./connectorHealth.mjs";
export type { ConnectorHealth } from "./connectorHealth.mjs";

interface SyncLogRow {
  id: string;
  entity_key: string;
  started_at: string;
  completed_at: string | null;
  rows_upserted: number;
  rows_deleted: number;
  status: "success" | "failed";
  error_detail: string | null;
}

export async function fetchEntityHealth(tenantId: string): Promise<EntityHealthRow[]> {
  // Pull the most recent sync per entity_key. Server-side DISTINCT ON
  // would be nicer; in PostgREST land we sort and dedupe client-side.
  // sync_log is small (one row per sync attempt, ~20 entities * a few
  // syncs per day) so paging through 200 rows is cheap.
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("sync_log")
    .select(
      "id, entity_key, started_at, completed_at, rows_upserted, rows_deleted, status, error_detail",
    )
    .eq("tenant_id", tenantId)
    .order("completed_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(`fetchEntityHealth failed: ${error.message}`);
  const rows = (data as SyncLogRow[] | null) ?? [];

  const latestByEntity = new Map<string, EntityHealthRow>();
  for (const r of rows) {
    if (latestByEntity.has(r.entity_key)) continue;
    latestByEntity.set(r.entity_key, {
      entityKey: r.entity_key,
      lastSyncStartedAt: r.started_at,
      lastSyncAt: r.completed_at,
      lastSyncStatus: r.status,
      rowsUpserted: r.rows_upserted,
      rowsDeleted: r.rows_deleted,
      lastErrorDetail: r.error_detail,
    });
  }
  return Array.from(latestByEntity.values()).sort((a, b) => a.entityKey.localeCompare(b.entityKey));
}

export async function fetchRecentSyncEvents(
  tenantId: string,
  limit = 20,
): Promise<RecentSyncEvent[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("sync_log")
    .select(
      "id, entity_key, started_at, completed_at, rows_upserted, rows_deleted, status, error_detail",
    )
    .eq("tenant_id", tenantId)
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(`fetchRecentSyncEvents failed: ${error.message}`);
  const rows = (data as SyncLogRow[] | null) ?? [];
  return rows.map((r) => ({
    id: r.id,
    entityKey: r.entity_key,
    startedAt: r.started_at,
    completedAt: r.completed_at,
    status: r.status,
    rowsUpserted: r.rows_upserted,
    rowsDeleted: r.rows_deleted,
    errorDetail: r.error_detail,
  }));
}

/**
 * MER-481: live row count for one entity's table, tenant-scoped.
 * `count: "estimated"` answers from the query planner's statistics
 * above the server's max-rows threshold (fast, never a full scan: the
 * MER-503 hang class) and exactly below it. Planner estimates can
 * drift from reality, which is why the UI tilde-prefixes anything in
 * the estimate band rather than presenting it as exact. 8s abort so
 * the probe can never hang the page. Returns null when the table is
 * missing, the key is unsafe, or the probe fails: the UI renders "-"
 * (unknown), never a fabricated 0.
 */
async function probeRowCount(table: string, tenantId: string): Promise<number | null> {
  try {
    const supabase = getSupabase();
    const { count, error } = await supabase
      .from(table)
      .select("*", { count: "estimated", head: true })
      .eq("tenant_id", tenantId)
      .abortSignal(AbortSignal.timeout(8000));
    if (error) {
      console.warn(`fetchEntityRowCount(${table}) failed: ${error.message}`);
      return null;
    }
    return typeof count === "number" ? count : null;
  } catch (err) {
    console.warn(
      `fetchEntityRowCount(${table}) failed: ${err instanceof Error ? err.message : String(err)}`,
    );
    return null;
  }
}

/**
 * MER-540: live row count for one entity's table, tenant-scoped, with
 * one retry on a transient failure. The MER-481 probe returns null
 * (renders "-") on ANY failure, timeout included, with no distinction
 * between "this entity genuinely has no live-count signal" and "the
 * PostgREST/network round trip hiccuped once." FINANCIAL_HEALTH
 * reading "-" the night after a clean 60-row sync fit the latter: a
 * single retry after a short backoff resolves a one-off blip without
 * hiding a real, repeatable failure (which still renders "-" after
 * the retry).
 */
export async function fetchEntityRowCount(
  tenantId: string,
  entityKey: string,
): Promise<number | null> {
  const table = entityTableFor(entityKey);
  if (!table) return null;
  const first = await probeRowCount(table, tenantId);
  if (first !== null) return first;
  await new Promise((resolve) => setTimeout(resolve, 500));
  return probeRowCount(table, tenantId);
}

export interface TableStats {
  lastAnalyze: string | null;
  lastAutoanalyze: string | null;
}

interface AdminTableStatsRow {
  table_name: string;
  n_live_tup: number | null;
  last_analyze: string | null;
  last_autoanalyze: string | null;
}

/**
 * MER-540: per-table planner-statistics age, staff-gated via the
 * admin_table_stats() RPC (migration 0078). Failures (non-staff
 * session, RPC unavailable) return an empty map rather than throwing,
 * so a stats-lookup hiccup degrades to "no staleness marker" instead
 * of breaking the Canonical Entities panel.
 */
export async function fetchTableStats(): Promise<Map<string, TableStats>> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("admin_table_stats");
  if (error) {
    console.warn(`fetchTableStats failed: ${error.message}`);
    return new Map();
  }
  const rows = (data as AdminTableStatsRow[] | null) ?? [];
  return new Map(
    rows.map((r) => [
      r.table_name,
      { lastAnalyze: r.last_analyze, lastAutoanalyze: r.last_autoanalyze },
    ]),
  );
}

/**
 * MER-540: is entityKey's live count stale relative to its last
 * successful sync? Looks up the entity's table in `tableStats` (from
 * fetchTableStats) and defers the actual comparison to
 * entityRows.isCountStale.
 */
export function isEntityCountStale(
  tableStats: Map<string, TableStats>,
  entityKey: string,
  lastSyncAt: string | null,
): boolean {
  const table = entityTableFor(entityKey);
  if (!table) return false;
  const stats = tableStats.get(table);
  if (!stats) return false;
  return isCountStale(stats.lastAnalyze, stats.lastAutoanalyze, lastSyncAt);
}

/**
 * MER-478: last N sync attempts for ONE entity, newest first. Powers
 * the expanded failed-row view on the Customer Detail page. entityKey
 * is matched exactly as stored (the pipeline writes UPPERCASE keys and
 * EntityHealthRow.entityKey carries the raw value, so pass it through).
 */
export async function fetchEntitySyncHistory(
  tenantId: string,
  entityKey: string,
  limit = 5,
): Promise<RecentSyncEvent[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("sync_log")
    .select(
      "id, entity_key, started_at, completed_at, rows_upserted, rows_deleted, status, error_detail",
    )
    .eq("tenant_id", tenantId)
    .eq("entity_key", entityKey)
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(`fetchEntitySyncHistory failed: ${error.message}`);
  const rows = (data as SyncLogRow[] | null) ?? [];
  return rows.map((r) => ({
    id: r.id,
    entityKey: r.entity_key,
    startedAt: r.started_at,
    completedAt: r.completed_at,
    status: r.status,
    rowsUpserted: r.rows_upserted,
    rowsDeleted: r.rows_deleted,
    errorDetail: r.error_detail,
  }));
}
